<script setup lang="ts">

import {computed, useTemplateRef} from "vue";
import {useContext, useDialogs, useHost} from "@hantera/portal-app";
import {DropdownDivider, DropdownItem, DropdownMenu} from "@hantera/design-system";

const context = useContext()
const profileMenuButton = useTemplateRef<HTMLDivElement>('profileMenuButton')
const profileMenu = useTemplateRef<InstanceType<typeof DropdownMenu>>('profileMenu')
const host = useHost()
const dialogs = useDialogs()

const isLocalDevEnabled = (<any>window).__HANTERA_APP_DEVELOPMENT__;

const hasPicture = computed(() => {
  const user = context.user();
  if (!user?.picture?.length) {
    return false;
  }
  return user.picture.length > 0
});

const pictureInitials = computed(() => {
  if (typeof context.user().name === 'string') {

    const [firstName, lastName] = context.user().name?.split(' ')
    return `${firstName[0]}${lastName?.[0] || ''}`
  }

  return ''
});

function openMenu() {
  profileMenu.value?.open()
}

function setTheme(value: 'system' | 'light' | 'dark') {
  (window as any).setTheme(value)
}

async function signOut() {

  if (await dialogs.confirm('Are you sure?', 'Sign out', "Yes", "No", true))
  {
    host.send({
      type: 'signOut'
    })
  }
}

function toggleLocalDev() {
  if (isLocalDevEnabled) {
    host.send({
      type: 'disableAppDev'
    });
  } else {
    host.send({
      type: 'enableAppDev'
    });
  }
}

function openPat() {
  context.openView('/pat', {})
}

function openUserSettings() {
  context.openView('/user', {})
}

</script>

<template>
  <button class="btn-reset profile" @click="openMenu" ref="profileMenuButton">
    <span v-if="hasPicture" class="profile-picture">
      <img :src="`base64://${context.user()?.picture}`" alt="Profile Picture" />
    </span>
    <span v-else class="profile-picture-initials">
      {{ pictureInitials }}
    </span>
  </button>

  <DropdownMenu style="width: 40px; height: 40px" ref="profileMenu" :target="profileMenuButton">
    <DropdownItem @click="openUserSettings">User Settings</DropdownItem>
    <DropdownItem @click="openPat">Personal Access Tokens (PAT)</DropdownItem>
    <DropdownItem @click="toggleLocalDev">{{ isLocalDevEnabled ? 'Disable' : 'Enable' }} local development</DropdownItem>
    <DropdownDivider/>
    <DropdownItem @click="setTheme('system')">Switch to system theme</DropdownItem>
    <DropdownItem @click="setTheme('light')">Switch to light theme</DropdownItem>
    <DropdownItem @click="setTheme('dark')">Switch to dark theme</DropdownItem>
    <DropdownDivider/>
    <DropdownItem @click="signOut">Sign out</DropdownItem>
  </DropdownMenu>
</template>

<style scoped>

.profile {
  height: 32px;
  width: 32px;
  border-radius: 50%;
  overflow: hidden;
  padding: 0;
  justify-self: center;
  border: 1px solid hsl(var(--color-0))
}

.profile:hover {
  border-color: hsl(var(--color-primary))
}

.profile-picture-initials {
  background-color: hsl(var(--color-70));
  color: hsl(var(--color-10));
  user-select: none;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: .8rem;
  font-weight: 500;
  line-height: 1em;
  height: 100%;
  width: 100%;
  text-transform: uppercase;
}

</style>